// Module and page structure for permission management.
// Every module and page carries `labelKey` (shell.moduleStructure.*). `label`
// is kept for existing consumers as a getter that translates at access time,
// so it follows the active language; prefer t(entry.labelKey) when rendering.
import i18n from '../i18n';

const withLabel = (entry, labelKey) =>
  Object.defineProperties(entry, {
    labelKey: { value: labelKey, enumerable: true },
    label: { get: () => i18n.t(labelKey), enumerable: true },
  });

const RAW_STRUCTURE = {
  hr: {
    icon: '👥',
    pages: {
      employees: { path: '/modules/hr/employees' },
      positions: { path: '/modules/hr/positions' },
      departments: { path: '/modules/hr/departments' },
      leave_types: { path: '/modules/hr/leave-types' },
      leave_requests: { path: '/modules/hr/leave-requests' },
      leave_approvals: { path: '/modules/hr/leave-approvals' },
      my_leaves: { path: '/modules/hr/my-leaves' },
      attendance: { path: '/modules/hr/attendance' },
      payroll: { path: '/modules/hr/payroll' }
    }
  },
  inventory: {
    icon: '📦',
    pages: {
      products: { path: '/modules/inventory/products' },
      warehouses: { path: '/modules/inventory/warehouses' },
      locations: { path: '/modules/inventory/locations' },
      categories: { path: '/modules/inventory/categories' },
      stock_movements: { path: '/modules/inventory/movements' },
      waybills: { path: '/modules/inventory/waybills' },
      stock_count: { path: '/modules/inventory/stock-count' },
      suppliers: { path: '/modules/inventory/suppliers' },
      purchase_orders: { path: '/modules/inventory/purchase-orders' },
      reports: { path: '/modules/inventory/reports' },
      abc_analysis: { path: '/modules/inventory/abc-analysis' },
      adjustment_reasons: { path: '/modules/inventory/adjustment-reasons' },
      advanced_reports: { path: '/modules/inventory/advanced-reports' },
      barcode_scanner: { path: '/modules/inventory/barcode-scanner' },
      bulk_import: { path: '/modules/inventory/bulk-import' },
      consignment_stock: { path: '/modules/inventory/consignment-stock' },
      cycle_counts: { path: '/modules/inventory/cycle-counts' },
      historical_valuation: { path: '/modules/inventory/historical-valuation' },
      intercompany_transfer: { path: '/modules/inventory/intercompany-transfer' },
      lot_tracking: { path: '/modules/inventory/lot-tracking' },
      movement_approvals: { path: '/modules/inventory/approvals' },
      picking_orders: { path: '/modules/inventory/picking-orders' },
      realtime_dashboard: { path: '/modules/inventory/realtime-dashboard' },
      reorder_suggestions: { path: '/modules/inventory/reorder-suggestions' },
      stock_forecasting: { path: '/modules/inventory/stock-forecasting' },
      stock_reservations: { path: '/modules/inventory/stock-reservations' },
      stock_valuation: { path: '/modules/inventory/stock-valuation' },
      transfer_approvals: { path: '/modules/inventory/transfer-approvals' },
      unit_conversions: { path: '/modules/inventory/unit-conversions' }
    }
  },
  sales: {
    icon: '💰',
    pages: {
      customers: { path: '/modules/sales/customers' },
      products: { path: '/modules/sales/products' },
      quotes: { path: '/modules/sales/quotes' },
      orders: { path: '/modules/sales/orders' },
      invoices: { path: '/modules/sales/invoices' },
      documents: { path: '/modules/sales/documents' },
      margin_analysis: { path: '/modules/sales/margin-analysis' }
    }
  },
  purchasing: {
    icon: '🛒',
    pages: {
      requests: { path: '/modules/purchasing/requests' },
      orders: { path: '/modules/purchasing/orders' },
      rfqs: { path: '/modules/purchasing/rfqs' },
      supplier_quotes: { path: '/modules/purchasing/supplier-quotes' },
      goods_receipts: { path: '/modules/purchasing/goods-receipts' }
    }
  },
  finance: {
    icon: '💳',
    pages: {
      // Banka Hesapları and Tahsilat Takibi are Ön Muhasebe's Banka/Kasa and
      // Yaşlandırma screens; these rights open them (routeAccess PAGE_FALLBACKS)
      bank_accounts: { path: '/modules/accounting/bank-cash' },
      transactions: { path: '/modules/finance/transactions' },
      credit_cards: { path: '/modules/finance/credit-cards' },
      loans: { path: '/modules/finance/loans' },
      letters_of_guarantee: { path: '/modules/finance/letters-of-guarantee' },
      categories: { path: '/modules/finance/categories' },
      // hidden: the screen only simulated bank transfers (kept for its stored records)
      bank_integration: { path: '/modules/finance/bank-integration', hidden: true },
      budget_management: { path: '/modules/finance/budget-management' },
      cash_flow: { path: '/modules/finance/cash-flow' },
      collection_tracking: { path: '/modules/accounting/aging' },
      currency_operations: { path: '/modules/finance/currency-operations' },
      financial_reports: { path: '/modules/finance/financial-reports' },
      investments: { path: '/modules/finance/investments' }
    }
  },
  accounting: {
    icon: '📊',
    pages: {
      chart_of_accounts: { path: '/modules/accounting/chart-of-accounts' },
      current_accounts: { path: '/modules/accounting/current-accounts' },
      expenses: { path: '/modules/accounting/expenses' },
      invoices: { path: '/modules/accounting/invoices' },
      pending_invoices: { path: '/modules/accounting/pending-invoices' },
      payments: { path: '/modules/accounting/payments' },
      cheques_notes: { path: '/modules/accounting/cheques-notes' },
      bank_cash: { path: '/modules/accounting/bank-cash' },
      reconciliation: { path: '/modules/accounting/reconciliation' },
      aging_analysis: { path: '/modules/accounting/aging' },
      income_statement: { path: '/modules/accounting/income-statement' },
      vat_report: { path: '/modules/accounting/vat-report' },
      reports: { path: '/modules/accounting/reports' },
      cash_flow: { path: '/modules/accounting/cash-flow' },
      parasut: { path: '/modules/accounting/integrations' }
    }
  },
  crm: {
    icon: '📞',
    pages: {
      dashboard: { path: '/modules/crm/dashboard' },
      leads: { path: '/modules/crm/leads' },
      opportunities: { path: '/modules/crm/opportunities' },
      contacts: { path: '/modules/crm/contacts' },
      activities: { path: '/modules/crm/activities' },
      tasks: { path: '/modules/crm/tasks' },
      campaigns: { path: '/modules/crm/campaigns' },
      email_templates: { path: '/modules/crm/email-templates' },
      pipeline: { path: '/modules/crm/pipeline' },
      reports: { path: '/modules/crm/reports' }
    }
  },
  service: {
    icon: '🔧',
    pages: {
      tickets: { path: '/modules/service/tickets' },
      customers: { path: '/modules/service/customers' },
      appointments: { path: '/modules/service/appointments' },
      spare_parts: { path: '/modules/service/spare-parts' },
      products: { path: '/modules/service/products' },
      calendar: { path: '/modules/service/calendar' },
      feedback: { path: '/modules/service/feedback' },
      product_categories: { path: '/modules/service/product-categories' },
      service_history: { path: '/modules/service/history' },
      warranty_claims: { path: '/modules/service/warranty-claims' },
      service_contracts: { path: '/modules/service/contracts' },
      maintenance_plans: { path: '/modules/service/maintenance-plans' },
      work_orders: { path: '/modules/service/work-orders' },
      reports: { path: '/modules/service/reports' }
    }
  },
  production: {
    icon: '🏭',
    pages: {
      production_orders: { path: '/modules/production/orders' },
      panels: { path: '/modules/production/panels' },
      production_planning: { path: '/modules/production/planning' },
      work_orders: { path: '/modules/production/work-orders' },
      bom: { path: '/modules/production/bom' },
      machines: { path: '/modules/production/machines' },
      shifts: { path: '/modules/production/shifts' },
      mrp: { path: '/modules/production/mrp' },
      quality_control: { path: '/modules/production/quality-control' },
      scrap: { path: '/modules/production/scrap' },
      production_cost: { path: '/modules/production/cost' },
      gantt_chart: { path: '/modules/production/gantt' },
      oee: { path: '/modules/production/oee' },
      reports: { path: '/modules/production/reports' }
    }
  },
  assets: {
    icon: '🏢',
    pages: {
      assets: { path: '/modules/assets/list' },
      categories: { path: '/modules/assets/categories' },
      assignments: { path: '/modules/assets/assignments' },
      maintenance: { path: '/modules/assets/maintenance' }
    }
  },
  office: {
    icon: '🏛️',
    pages: {
      announcements: { path: '/modules/office/announcements' },
      vehicles: { path: '/modules/office/vehicles' },
      visitors: { path: '/modules/office/visitors' },
      supplies: { path: '/modules/office/supplies' },
      meeting_rooms: { path: '/modules/office/meeting-rooms' },
      maintenance: { path: '/modules/office/maintenance' },
      policies: { path: '/modules/office/policies' }
    }
  },
  tasks: {
    icon: '✅',
    pages: {
      kanban: { path: '/modules/tasks/kanban' }
    }
  },
  tender: {
    icon: '📋',
    pages: {
      tenders: { path: '/modules/tender/tenders' },
      bids: { path: '/modules/tender/bids' },
      guarantees: { path: '/modules/tender/guarantees' },
      evaluation: { path: '/modules/tender/evaluation' },
      reports: { path: '/modules/tender/reports' },
      objections: { path: '/modules/tender/objections' },
      documents: { path: '/modules/tender/documents' },
      commission: { path: '/modules/tender/commission' },
      live_auction: { path: '/modules/tender/live-auction' },
      supplier_management: { path: '/modules/tender/supplier-management' }
    }
  },
  reservations: {
    icon: '🗓️',
    pages: {
      bookings: { path: '/modules/reservations' },
      resources: { path: '/modules/reservations' }
    }
  },
  insurance: {
    icon: '🛡️',
    pages: {
      panel: { path: '/modules/insurance' },
      policies: { path: '/modules/insurance' },
      insurers: { path: '/modules/insurance' }
    }
  },
  education: {
    icon: '🎓',
    pages: {
      courses: { path: '/modules/education' },
      students: { path: '/modules/education' },
      enrollments: { path: '/modules/education' },
      attendance: { path: '/modules/education' }
    }
  },
  membership: {
    icon: '🎟️',
    pages: {
      members: { path: '/modules/membership' },
      memberships: { path: '/modules/membership' },
      packages: { path: '/modules/membership' },
      checkins: { path: '/modules/membership' }
    }
  },
  ledger: {
    icon: '📒',
    pages: {
      journal: { path: '/modules/ledger/journal' },
      accounts: { path: '/modules/ledger/accounts' },
      reports: { path: '/modules/ledger/reports' }
    }
  },
  projects: {
    icon: '🏗️',
    pages: {
      projects: { path: '/modules/projects' }
    }
  }
};

export const MODULE_STRUCTURE = Object.fromEntries(
  Object.entries(RAW_STRUCTURE).map(([moduleName, moduleData]) => {
    const base = `shell.moduleStructure.${moduleName}`;
    Object.entries(moduleData.pages).forEach(([pageName, pageData]) =>
      withLabel(pageData, `${base}.pages.${pageName}`),
    );
    return [moduleName, withLabel(moduleData, `shared.moduleNames.${moduleName}`)];
  }),
);

// Helper function to get all modules
export const getAllModules = () => {
  return Object.keys(MODULE_STRUCTURE);
};

// Helper function to get pages of a module
export const getModulePages = (moduleName) => {
  return MODULE_STRUCTURE[moduleName]?.pages || {};
};

// Helper function to get full page path
export const getPageFullPath = (moduleName, pageName) => {
  return `${moduleName}.${pageName}`;
};

// "module.page" permission key of the screen a link points at, or null when
// the path is not a permission-managed page (query and hash are ignored).
export const pageKeyForPath = (link) => {
  const path = (link || '').split(/[?#]/)[0].replace(/\/+$/, '');
  for (const [moduleName, moduleData] of Object.entries(MODULE_STRUCTURE)) {
    for (const [pageName, pageData] of Object.entries(moduleData.pages)) {
      if (pageData.path === path) return `${moduleName}.${pageName}`;
    }
  }
  return null;
};
